import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AddServicesNotification from './AddServicesNotification';
import PermissionsNotification from './PermissionsNotification';

const initialState = [];

const NotificationsContext = React.createContext(initialState);

export function NotificationsContextProvider({ children }) {
  const reduxStore = useSelector(state => state, shallowEqual);
  const [notifications, setNotifications] = useState(initialState);
  const { t } = useTranslation('account');

  const history = useHistory();

  const addServices = useMemo(() => ({
    prompt: <AddServicesNotification />,
    action: () => history.push('/datasources'),
    read: false,
  }), [history]);

  const gotoConsents = useCallback(() => history.push('/sharing#consents'), [history]);
  
  const { consents = {}, session = {} } = reduxStore;
  const { user = {} } = session;
  const { services = [] } = user;

  useEffect(() => {
    const newNotifications = [ ...notifications ];
    let modified = false;
    if (services.length) {
      let i;
      do {
        i = newNotifications.indexOf(addServices);
        if (i >= 0) {
          newNotifications.splice(i, 1);
          modified = true;
        }
      } while (i >= 0);
    } else {
      const i = newNotifications.indexOf(addServices);
      if (i < 0) {
        newNotifications.push(addServices);
        modified = true;
      }
    }

    Object.keys(consents).forEach((id) => {
      const c = consents[id];
      const { instances = [] } = c;
      const lastInstance = instances[0];
      if (lastInstance.requested && !lastInstance.given && !lastInstance.denied) {
        if (!newNotifications.some(n => n.consent === id)) {
          newNotifications.push({
            prompt: <PermissionsNotification c={c} />,
            action: gotoConsents,
            read: false,
            consent: id,
          });
          modified = true;
        }
      }
    });
    if (modified) {
      setNotifications(newNotifications);
    }
  }, [addServices, gotoConsents, consents, notifications, services, setNotifications, t]);

  const state = Object.freeze({
    notifications,
    setNotifications,
  });
  
  return (
    <NotificationsContext.Provider value={state}>
      { children }
    </NotificationsContext.Provider>
  );
}

export default NotificationsContext;
